/**
 * Event Forms Service
 * 
 * Handles all event form related API calls to the DAB backend
 */

import { getDabUrl } from './api';

export interface Participant {
    name: string;
    workstream: string;
    roleInterest: string;
    fundingSource: string;
}

export interface CostItem {
    category: string;
    details: string;
    estimatedCost: string;
}

export interface EventFormClient {
    id?: number;
    submitter_email: string;
    submitter_name: string;
    submission_date?: string;
    status: 'pending' | 'approved' | 'rejected';

    // Section 1: Event Details
    event_title: string;
    dates: string;
    location: string;
    event_organisers?: string;
    type_of_event?: string;
    event_overview?: string;

    // Section 2: Rationale for Participation
    relevance_to_iod?: string;
    expected_benefits?: string;
    proposed_outputs?: string;

    // Section 3: Participants (stored as JSON string)
    participants?: string;

    // Section 4: Estimated Costs (stored as JSON string)
    costs?: string;
    total_cost?: number;

    // Admin-assigned financial year (calendar year the May-April FY starts)
    financial_year_start?: number | null;

    // Section 5: Communications and Follow up
    planned_communications?: string;
    lead_for_communications?: string;
    follow_up_actions?: string;

    // Approval fields
    approved_by?: string;
    approval_date?: string;
    rejection_reason?: string;

    // Audit fields
    created_at?: string;
    updated_at?: string;
}

export interface EventFormServer {
    id?: number;
    submitter_email: string;
    submitter_name: string;
    submission_date?: string;
    status: string;
    event_title: string;
    dates: string;
    location: string;
    event_organisers?: string;
    type_of_event?: string;
    event_overview?: string;
    relevance_to_iod?: string;
    expected_benefits?: string;
    proposed_outputs?: string;
    participants?: string;
    costs?: string;
    total_cost?: number;
    financial_year_start?: number | null;
    planned_communications?: string;
    lead_for_communications?: string;
    follow_up_actions?: string;
    approved_by?: string;
    approval_date?: string;
    rejection_reason?: string;
    created_at?: string;
    updated_at?: string;
}

/**
 * Convert server format to client format
 */
export function fromServerEventForm(server: EventFormServer): EventFormClient {
    return {
        ...server,
        status: server.status as 'pending' | 'approved' | 'rejected'
    };
}

/**
 * Convert client format to server format
 */
export function toServerEventForm(client: EventFormClient): EventFormServer {
    return {
        ...client,
        status: client.status
    };
}

/**
 * Submit a new event form
 */
export async function submitEventForm(eventForm: EventFormClient): Promise<EventFormClient> {
    console.log('Submitting event form:', eventForm);

    const response = await fetch(getDabUrl('EventForms'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(toServerEventForm(eventForm))
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to submit event form:', response.status, errorText);
        throw new Error(`Failed to submit event form: ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    console.log('Event form submitted successfully:', data);
    return fromServerEventForm(data.value[0]);
}

/**
 * Update an existing event form
 */
export async function updateEventForm(id: number, eventForm: Partial<EventFormClient>): Promise<EventFormClient> {
    console.log('Updating event form:', id, 'with data:', eventForm);

    const response = await fetch(getDabUrl(`EventForms/id/${id}`), {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(eventForm)
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to update event form:', response.status, errorText);
        throw new Error(`Failed to update event form: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Event form updated successfully:', data);
    return fromServerEventForm(data.value[0]);
}

/**
 * Delete an event form (admin only)
 */
export async function deleteEventForm(id: number): Promise<void> {
    const response = await fetch(getDabUrl(`EventForms/id/${id}`), {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    });

    if (!response.ok) {
        throw new Error(`Failed to delete event form: ${response.statusText}`);
    }
}

/**
 * Fetch all event forms (admin only)
 */
export async function fetchAllEventForms(status?: 'pending' | 'approved' | 'rejected'): Promise<EventFormClient[]> {
    let queryParams = '$orderby=submission_date desc';

    if (status) {
        queryParams += `&$filter=status eq '${status}'`;
    }

    const response = await fetch(getDabUrl(`EventForms?${queryParams}`), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch all event forms: ${response.statusText}`);
    }

    const data = await response.json();
    return data.value.map((ef: EventFormServer) => fromServerEventForm(ef));
}

/**
 * Fetch a single event form by ID
 */
export async function fetchEventFormById(id: number): Promise<EventFormClient | null> {
    const url = getDabUrl(`EventForms/id/${id}`);
    console.log('Fetching event form from URL:', url);

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    });

    if (!response.ok) {
        console.error('Failed to fetch event form:', response.status, response.statusText);
        throw new Error(`Failed to fetch event form: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Raw response data:', data);

    if (data.value && Array.isArray(data.value)) {
        return data.value.length > 0 ? fromServerEventForm(data.value[0]) : null;
    }

    return data ? fromServerEventForm(data) : null;
}

/**
 * Approve an event form (admin only)
 */
export async function approveEventForm(id: number, approvedBy: string): Promise<EventFormClient> {
    return await updateEventForm(id, {
        status: 'approved',
        approved_by: approvedBy,
        approval_date: new Date().toISOString()
    });
}

/**
 * Update an event's total cost (admin only, post-submission)
 */
export async function updateEventCost(id: number, cost: number): Promise<EventFormClient> {
    return await updateEventForm(id, { total_cost: cost });
}

/**
 * Update an event's admin-assigned financial year (admin only)
 */
export async function updateEventFinancialYear(id: number, financialYearStart: number): Promise<EventFormClient> {
    return await updateEventForm(id, { financial_year_start: financialYearStart });
}

/**
 * Reject an event form (admin only)
 */
export async function rejectEventForm(id: number, rejectionReason: string): Promise<EventFormClient> {
    return await updateEventForm(id, {
        status: 'rejected',
        rejection_reason: rejectionReason
    });
}

/**
 * Parse participants JSON string to array
 */
export function parseParticipants(participantsJson?: string): Participant[] {
    if (!participantsJson) return [];
    try {
        return JSON.parse(participantsJson);
    } catch {
        return [];
    }
}

/**
 * Parse costs JSON string to array
 */
export function parseCosts(costsJson?: string): CostItem[] {
    if (!costsJson) return [];
    try {
        return JSON.parse(costsJson);
    } catch {
        return [];
    }
}
